import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PermissionDeclarationSchema,
  RoleSummarySchema,
  SensitivityCategorySchema,
  type PermissionDeclarationDto,
  type PermissionGrantSummaryDto,
  type RoleSummaryDto,
} from '@taqon/contracts';
import { z } from 'zod';
import { getData, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import Link from '@mui/material/Link';
import { Link as RouterLink, useNavigate } from 'react-router';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { GrantToRolesDialog, type GrantedToRoles } from './GrantToRolesDialog';

export const ROLES = '/api/v1/platform/roles';
const CreatedSchema = z.object({ id: z.string() });
const CATEGORIES = SensitivityCategorySchema.options;
export const grantsKey = (code: string) => ['platform', 'roles', code, 'grants'];

/**
 * Role administration (SDD §11.4, §11.5; FRD-USR-001, USR-005): the roles, each opening its own
 * page (`/admin/roles/:code`) with its permissions and holders, and creating a role. Administration
 * permissions work only through a privileged role, which the screen states rather than enforces:
 * the API is the enforcement point.
 */
export function RolesPage() {
  const session = useSession();
  const canCreateRole = hasCapability(session.data, 'platform.role:create');
  const canGrant = hasCapability(session.data, 'platform.permission_grant:create');
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [granting, setGranting] = useState(false);
  const [granted, setGranted] = useState<GrantedToRoles | null>(null);

  const roles = useQuery({
    queryKey: ['platform', 'roles'],
    queryFn: () => getData(ROLES, z.array(RoleSummarySchema)),
  });
  const list = roles.data ?? [];

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Roles & permissions"
        description="Roles group permissions. A user's access is the combination of the roles assigned to them."
        actions={
          canCreateRole || canGrant ? (
            <>
              {canGrant && (
                <Button
                  variant="outlined"
                  onClick={(event) => {
                    event.currentTarget.focus();
                    setGranted(null);
                    setGranting(true);
                  }}
                >
                  Grant to several roles
                </Button>
              )}
              {canCreateRole && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={(event) => {
                    event.currentTarget.focus();
                    setCreating(true);
                  }}
                >
                  New role
                </Button>
              )}
            </>
          ) : undefined
        }
      />
      {granted !== null && (
        <Alert
          severity="success"
          variant="outlined"
          onClose={() => {
            setGranted(null);
          }}
        >
          {granted.granted.length > 0
            ? `${granted.permission} granted to ${granted.granted.join(', ')}.`
            : `No role needed ${granted.permission}.`}
          {granted.skipped.length > 0 && ` Already held by ${granted.skipped.join(', ')}.`}
        </Alert>
      )}
      {roles.isError && <ErrorState message={roles.error.message} />}
      {roles.isPending && <LoadingState label="Loading roles" />}
      {roles.data !== undefined && list.length === 0 && (
        <Section>
          <EmptyState title="No roles yet" />
        </Section>
      )}
      {list.length > 0 && (
        <Section title="Roles">
          <Table size="small" aria-label="Roles">
            <TableHead>
              <TableRow>
                <TableCell>Role</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Permissions</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((role) => (
                <TableRow key={role.code} hover>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={`/admin/roles/${encodeURIComponent(role.code)}`}
                      sx={{ fontWeight: 600 }}
                    >
                      {role.name}
                    </Link>
                  </TableCell>
                  <TableCell>{role.code}</TableCell>
                  <TableCell>
                    {role.isPrivileged ? 'Privileged administration' : 'Business'}
                  </TableCell>
                  <TableCell align="right">{role.grantCount}</TableCell>
                  <TableCell>
                    <StatusChip status={role.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>
      )}
      <GrantToRolesDialog
        open={granting}
        roles={list}
        onClose={(result) => {
          setGranting(false);
          if (result !== undefined) setGranted(result);
        }}
      />
      <CreateRoleDialog
        open={creating}
        onClose={(code) => {
          setCreating(false);
          // The new role opens on its own page, ready for its permissions.
          if (code !== undefined) void navigate(`/admin/roles/${encodeURIComponent(code)}`);
        }}
      />
    </Stack>
  );
}

/** `finance.supplier_invoice` → `Finance · Supplier invoice`: presentation only. */
export function humanRecord(recordType: string): string {
  const [module = '', record = ''] = recordType.split('.');
  const words = (value: string) => value.replace(/_/g, ' ');
  const title = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
  return `${MODULE_NAMES[module] ?? title(words(module))} · ${title(words(record))}`;
}

/** Module codes whose display name is not their capitalised code. */
const MODULE_NAMES: Readonly<Record<string, string>> = {
  hr: 'HR',
  crm: 'CRM',
  vat: 'VAT',
};

export function GrantDialog({
  role,
  existing,
  open,
  onClose,
}: {
  role: RoleSummaryDto;
  existing: readonly PermissionGrantSummaryDto[];
  open: boolean;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [permission, setPermission] = useState<PermissionDeclarationDto | null>(null);
  const [category, setCategory] = useState('');
  const [ownRecordsOnly, setOwnRecordsOnly] = useState(false);
  const catalogue = useQuery({
    queryKey: ['platform', 'permission-catalogue'],
    enabled: open,
    queryFn: () =>
      getData('/api/v1/platform/permission-catalogue', z.array(PermissionDeclarationSchema)),
  });
  const held = new Set(
    existing
      .filter((g) => g.field === null && g.sensitivityCategory === null)
      .map((g) => `${g.recordType}:${g.action}`),
  );
  const options = (catalogue.data ?? []).filter(
    (p) => p.action === 'view_unmasked' || !held.has(`${p.recordType}:${p.action}`),
  );
  const unmasked = permission?.action === 'view_unmasked';
  // Self-service (SDD §11.4 step 3) narrows a business permission to the holder's own records. It
  // does not apply to administration or to unmasking, which the API refuses.
  const canLimitToOwn = permission !== null && !permission.privileged && !unmasked;

  const grant = useMutation({
    mutationFn: () =>
      postJson(
        `${ROLES}/${encodeURIComponent(role.code)}/permission-grants`,
        {
          recordType: permission?.recordType ?? '',
          action: permission?.action ?? '',
          ...(unmasked && category !== '' ? { sensitivityCategory: category } : {}),
          ...(canLimitToOwn && ownRecordsOnly ? { ownRecordsOnly: true } : {}),
        },
        CreatedSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      setPermission(null);
      setCategory('');
      setOwnRecordsOnly(false);
      onClose();
      await queryClient.invalidateQueries({ queryKey: grantsKey(role.code) });
      await queryClient.invalidateQueries({ queryKey: ['platform', 'roles'] });
    },
  });

  return (
    <FormDialog
      open={open}
      title={`Grant a permission to ${role.name}`}
      submitLabel="Grant permission"
      canSubmit={permission !== null && (!unmasked || category !== '')}
      submitting={grant.isPending}
      error={
        grant.isError ? grant.error.message : catalogue.isError ? catalogue.error.message : null
      }
      onClose={() => {
        grant.reset();
        onClose();
      }}
      onSubmit={() => {
        grant.mutate();
      }}
    >
      <Autocomplete
        options={options}
        value={permission}
        onChange={(_, value) => {
          setPermission(value);
          setCategory('');
        }}
        groupBy={(option) =>
          option.owningModule.charAt(0).toUpperCase() + option.owningModule.slice(1)
        }
        getOptionLabel={(option) => option.description}
        isOptionEqualToValue={(a, b) => a.recordType === b.recordType && a.action === b.action}
        loading={catalogue.isPending}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Permission"
            required
            helperText="Search by what the permission allows, for example “Supplier: view”."
          />
        )}
      />
      {unmasked && (
        <TextField
          select
          label="Sensitivity category"
          required
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
          }}
          helperText="Unmasked access is granted one sensitivity category at a time."
        >
          {CATEGORIES.map((value) => (
            <MenuItem key={value} value={value}>
              {value.replace(/_/g, ' ')}
            </MenuItem>
          ))}
        </TextField>
      )}
      {canLimitToOwn && (
        <FormControlLabel
          control={
            <Checkbox
              checked={ownRecordsOnly}
              onChange={(e) => {
                setOwnRecordsOnly(e.target.checked);
              }}
            />
          }
          label="Own records only — reaches only the holder's own employee record (self-service)"
        />
      )}
      {permission?.privileged === true && !role.isPrivileged && (
        <Alert severity="warning" variant="outlined">
          This is an administration permission. It only takes effect through a privileged role, and{' '}
          {role.name} is not privileged.
        </Alert>
      )}
    </FormDialog>
  );
}

function CreateRoleDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: (createdCode?: string) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [privileged, setPrivileged] = useState(false);
  const create = useMutation({
    mutationFn: () =>
      postJson(
        ROLES,
        {
          code: code.trim(),
          name: name.trim(),
          ...(description.trim() !== '' ? { description: description.trim() } : {}),
          isPrivileged: privileged,
        },
        CreatedSchema.extend({ code: z.string() }),
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async (role) => {
      setCode('');
      setName('');
      setDescription('');
      setPrivileged(false);
      await queryClient.invalidateQueries({ queryKey: ['platform', 'roles'] });
      onClose(role.code);
    },
  });
  return (
    <FormDialog
      open={open}
      title="New role"
      submitLabel="Create role"
      canSubmit={code.trim() !== '' && name.trim() !== ''}
      submitting={create.isPending}
      error={create.isError ? create.error.message : null}
      onClose={() => {
        create.reset();
        onClose();
      }}
      onSubmit={() => {
        create.mutate();
      }}
    >
      <TextField
        label="Name"
        required
        autoFocus
        value={name}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
      <TextField
        label="Code"
        required
        value={code}
        onChange={(e) => {
          setCode(e.target.value.toLowerCase());
        }}
        helperText="A permanent identifier: lower case with underscores, for example finance_clerk."
      />
      <TextField
        label="Description"
        multiline
        minRows={2}
        value={description}
        onChange={(e) => {
          setDescription(e.target.value);
        }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={privileged}
            onChange={(e) => {
              setPrivileged(e.target.checked);
            }}
          />
        }
        label="Privileged administration role"
      />
      {privileged && (
        <Alert severity="warning" variant="outlined">
          Only a privileged role can carry administration permissions (users, roles, permissions and
          security configuration). Every use is audited.
        </Alert>
      )}
    </FormDialog>
  );
}
